import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useDialog from "../hooks/useDialog";
import {
  PURPOSES,
  assignmentProblem,
  assignmentRequest,
  newOperationId,
  purposeLabel,
} from "../data/assignments";
import { createAssignment, readClassroomLearners } from "../data/teacherWorkApi";
import { announceWorkspaceChange } from "../data/usePolledResource";
import { CheckIcon, CloseIcon } from "./Icons";
import ui from "./ui.module.css";

// Activity → Assign → what kind → who → when → Send (Handoff 007 §9).
// One sheet, one obvious action. Nothing says "sent" until the gateway has
// confirmed the assignment; a retry sends the same operation id, so a lost
// answer never makes a second assignment.
const AssignDialog = ({ classroomId, className, activity, onClose }) => {
  const titleId = useId();
  const closeRef = useRef(null);
  useDialog(onClose, closeRef);
  // One id for this send, however often it is retried.
  const [operationId] = useState(newOperationId);
  const [form, setForm] = useState({
    purpose: "homework",
    audience: "classroom",
    learnerIds: [],
    opensAt: "",
    dueAt: "",
  });
  const [learners, setLearners] = useState({ status: "idle", items: [] });
  // Bumped by "Try again"; the class list is read once per request.
  const [learnerRequest, setLearnerRequest] = useState(0);
  const [send, setSend] = useState({ status: "idle", problem: null, assignment: null });

  const choosing = form.audience === "selected_learners";
  useEffect(() => {
    if (!choosing) return undefined;
    let active = true;
    setLearners((current) =>
      current.status === "ready" ? current : { status: "loading", items: [] }
    );
    readClassroomLearners(classroomId).then(
      (data) => active && setLearners({ status: "ready", items: data.learners || [] }),
      () => active && setLearners({ status: "error", items: [] })
    );
    return () => {
      active = false;
    };
  }, [choosing, classroomId, learnerRequest]);

  // Once sent, the choices are fixed: changing them would be another send.
  const locked = send.status === "sending" || send.status === "sent";
  const edit = (field, value) => {
    if (locked) return;
    setForm((current) => ({ ...current, [field]: value }));
    setSend((current) => ({ ...current, problem: null }));
  };
  const toggleLearner = (id) =>
    edit(
      "learnerIds",
      form.learnerIds.includes(id) ? form.learnerIds.filter((x) => x !== id) : [...form.learnerIds, id]
    );

  const submit = async (event) => {
    event.preventDefault();
    if (locked) return;
    const { problem, request } = assignmentRequest(form, { operationId, activityId: activity.id });
    if (problem) {
      setSend({ status: "idle", problem, assignment: null });
      return;
    }
    setSend({ status: "sending", problem: null, assignment: null });
    try {
      const assignment = await createAssignment(classroomId, request);
      setSend({ status: "sent", problem: null, assignment });
      announceWorkspaceChange();
    } catch (error) {
      setSend({ status: "failed", problem: assignmentProblem(error), assignment: null });
    }
  };

  return (
    <div className={ui.sheetBackdrop} role="presentation">
      <section className={`${ui.sheet} ${ui.assignSheet}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className={ui.row}>
          <div>
            <h2 id={titleId}>Set work for later</h2>
            <p className={ui.meta}>{[activity.title, className].filter(Boolean).join(" · ")}</p>
          </div>
          <button ref={closeRef} type="button" className={ui.iconButton} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        {send.status === "sent" ? (
          <div className={ui.stack} role="status">
            <span className={ui.pill}>
              <CheckIcon size={16} />
              {purposeLabel(send.assignment?.purpose)} sent
            </span>
            <p>
              {form.audience === "classroom" ? "The whole class" : `${form.learnerIds.length} learner${form.learnerIds.length === 1 ? "" : "s"}`}{" "}
              can find it on their tablets
              {send.assignment?.opensAt ? " from the start date" : " now"}.
            </p>
            <div className={ui.sheetActions}>
              <Link to={`/work/assignments/${send.assignment.id}`} className={`${ui.button} ${ui.secondary}`}>
                See progress
              </Link>
              <button type="button" className={`${ui.button} ${ui.primary}`} onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <form className={ui.stack} onSubmit={submit} noValidate>
            <fieldset className={ui.choiceGroup} disabled={locked}>
              <legend>What kind of work?</legend>
              {PURPOSES.map((purpose) => (
                <label key={purpose.value} className={ui.choiceRow}>
                  <input
                    type="radio"
                    name="purpose"
                    value={purpose.value}
                    checked={form.purpose === purpose.value}
                    onChange={() => edit("purpose", purpose.value)}
                  />
                  <span>
                    <strong>{purpose.label}</strong>
                    <span className={ui.meta}> · {purpose.help}</span>
                  </span>
                </label>
              ))}
            </fieldset>

            <fieldset className={ui.choiceGroup} disabled={locked}>
              <legend>Who is it for?</legend>
              <label className={ui.choiceRow}>
                <input
                  type="radio"
                  name="audience"
                  checked={form.audience === "classroom"}
                  onChange={() => edit("audience", "classroom")}
                />
                <span>The whole class</span>
              </label>
              <label className={ui.choiceRow}>
                <input
                  type="radio"
                  name="audience"
                  checked={choosing}
                  onChange={() => edit("audience", "selected_learners")}
                />
                <span>Some learners</span>
              </label>
              {choosing && learners.status === "loading" && <p role="status">Loading learners…</p>}
              {choosing && learners.status === "error" && (
                <p className={ui.error} role="alert">
                  Learners could not be loaded.{" "}
                  <button type="button" className={ui.textButton} onClick={() => setLearnerRequest((n) => n + 1)}>
                    Try again
                  </button>
                </p>
              )}
              {choosing && learners.status === "ready" && (
                <ul className={ui.checkList}>
                  {learners.items.map((learner) => (
                    <li key={learner.id}>
                      <label className={ui.choiceRow}>
                        <input
                          type="checkbox"
                          checked={form.learnerIds.includes(learner.id)}
                          onChange={() => toggleLearner(learner.id)}
                        />
                        <span>{learner.name}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </fieldset>

            <fieldset className={ui.choiceGroup} disabled={locked}>
              <legend>When?</legend>
              <label>
                Start
                <input
                  type="datetime-local"
                  value={form.opensAt}
                  onChange={(event) => edit("opensAt", event.target.value)}
                />
                <span className={ui.meta}>Leave empty to start now.</span>
              </label>
              <label>
                Due
                <input
                  type="datetime-local"
                  value={form.dueAt}
                  onChange={(event) => edit("dueAt", event.target.value)}
                />
                <span className={ui.meta}>Optional. After this, learners who have not started can't start.</span>
              </label>
            </fieldset>

            {send.problem && (
              <p className={ui.error} role="alert">
                {send.problem}
              </p>
            )}
            <div className={ui.sheetActions}>
              <button type="button" className={`${ui.button} ${ui.secondary}`} onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className={`${ui.button} ${ui.primary}`} disabled={send.status === "sending"}>
                {send.status === "sending" ? "Sending…" : send.status === "failed" ? "Send again" : "Send"}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
};

export default AssignDialog;
